import {declarativeWriteRESTObjsToApollo, writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import * as ManagedModelsReduxActions from 'models/ManagedModelsReduxActions';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import {requestListV2Completed} from 'requests/RequestReduxActions';
import * as SampleActions from 'samples/SampleActions';
import {dispatchStorableSummariesLoaded} from 'samples/StorableActions';
import {populateSearchParamWithDefaultSort} from 'search/getSortsOrDefault';
import * as GlobalSearchDAO from 'search/GlobalSearchDAO';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {
  AsyncSearchResponse,
  SearchParams,
  SearchResponse,
  SearchResults,
  SearchStatus,
  SearchType,
} from 'search/SearchConstants';

export async function listItems(
  searchParams: SearchParams,
  type: SearchType,
  requestConsistency?: boolean
): Promise<SearchResponse> {
  const response: GlobalSearchResponse | AsyncSearchResponse = await doGlobalSearch(
    searchParams,
    type,
    `listing:${type}`,
    requestConsistency
  );

  if (response.status !== SearchStatus.COMPLETE) {
    return {
      searchId: response.searchId,
      status: response.status,
      errorMessage: response.errorMessage,
    };
  }

  return globalSearchResponseToSearchResults(response);
}

export function globalSearchResponseToSearchResults(response: GlobalSearchResponse): SearchResults {
  const {
    searchId,
    status,
    ids,
    groupItemIds,
    itemResults,
    total,
    isExact,
    timedOut,
    correlationId,
    attributes,
    groups,
    nextToken,
    analysesById,
    analyticsDashboardsById,
    batchesById,
    benchlingAppsById,
    bulkAssembliesById,
    canvasFolderArtifactsById = {},
    collectiblesById,
    connectionsById,
    datasetsById,
    entrySummariesById,
    filesById,
    fileSummariesById,
    foldersById,
    jellyfishesById,
    permissionsByModelId,
    processOutputsById,
    processTasksById,
    projectsById,
    proteinAlignmentSummariesGraphQLById,
    processesById,
    protocolSummariesById,
    requestedTaskSchemaIdsByRequestId,
    requestsById,
    requestV2SubmissionsById,
    runsById,
    sampleStatusCountsByRequestId,
    scoresById,
    sequenceAlignmentSummariesGraphQLById,
    stageEntrySummariesGraphQLById,
    storableSummariesById,
    templateCollectionsById,
    testDefinitionsById,
    testOrdersById,
    workflowSummariesById,
    worklistSummariesById,
    studiesById,
    worksheetsById,
  } = response;

  getStore().dispatch(ModelActions.entriesFetchCompleted(entrySummariesById));
  SampleActions.handleBatchesLoaded(Object.values(batchesById));
  getStore().dispatch(ModelActions.filesFetchCompleted(fileSummariesById));
  getStore().dispatch(
    ManagedModelsReduxActions.modelFetched(ManagedModelType.PROTOCOL_SUMMARY, protocolSummariesById)
  );
  getStore().dispatch(ModelActions.workflowSummariesFetchCompleted(workflowSummariesById));
  writeFragmentsToCache(getFragmentWrapper('ProjectSummary'), Object.values(projectsById));
  getStore().dispatch(ManagedModelsReduxActions.modelFetched(ManagedModelType.FOLDER_BY_ID, foldersById));
  dispatchStorableSummariesLoaded(Object.values(storableSummariesById));
  getStore().dispatch(
    ManagedModelsReduxActions.modelFetched(ManagedModelType.PERMISSIONS_BY_ITEM_ID, permissionsByModelId)
  );
  getStore().dispatch(
    requestListV2Completed({
      requests: Object.values(requestsById),
      sampleStatusCountsByRequestId,
      requestedTaskSchemaIdsByRequestId,
    })
  );
  writeFragmentsToCache(getFragmentWrapper('AnalysisSearchAttributes'), Object.values(analysesById));
  writeFragmentsToCache(
    getFragmentWrapper('AnalyticsDashboardSearchAttributes'),
    Object.values(analyticsDashboardsById)
  );
  writeFragmentsToCache(getFragmentWrapper('ProcessListing'), Object.values(processesById));
  writeFragmentsToCache(getFragmentWrapper('ProcessOutputListing'), Object.values(processOutputsById));
  writeFragmentsToCache(getFragmentWrapper('ProcessTaskListing'), Object.values(processTasksById));
  writeFragmentsToCache(getFragmentWrapper('ConnectionListing'), Object.values(connectionsById));
  writeFragmentsToCache(getFragmentWrapper('BulkAssemblyListing'), Object.values(bulkAssembliesById));
  writeFragmentsToCache(
    getFragmentWrapper('CanvasFolderArtifactListing'),
    Object.values(canvasFolderArtifactsById)
  );
  writeFragmentsToCache(
    getFragmentWrapper('SequenceAnalysisSummary'),
    Object.values(sequenceAlignmentSummariesGraphQLById)
  );
  writeFragmentsToCache(
    getFragmentWrapper('AaSequenceAlignmentSummary'),
    Object.values(proteinAlignmentSummariesGraphQLById)
  );
  writeFragmentsToCache(getFragmentWrapper('WorklistSummary'), Object.values(worklistSummariesById));
  writeFragmentsToCache(getFragmentWrapper('JellyfishListing'), Object.values(jellyfishesById));
  writeFragmentsToCache(getFragmentWrapper('StudyListing'), Object.values(studiesById));
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), Object.values(benchlingAppsById));
  writeFragmentsToCache(getFragmentWrapper('WorksheetListing'), Object.values(worksheetsById));
  writeFragmentsToCache(getFragmentWrapper('DatasetListing'), Object.values(datasetsById));
  writeFragmentsToCache(
    getFragmentWrapper('TemplateCollectionListing'),
    Object.values(templateCollectionsById)
  );
  writeFragmentsToCache(getFragmentWrapper('BaseCollectibleSummary'), Object.values(collectiblesById));
  writeFragmentsToCache(getFragmentWrapper('FileListing'), Object.values(filesById));
  writeFragmentsToCache(getFragmentWrapper('TestDefinitionListing'), Object.values(testDefinitionsById));
  writeFragmentsToCache(
    getFragmentWrapper('RequestV2SubmissionListing'),
    Object.values(requestV2SubmissionsById)
  );
  writeFragmentsToCache(getFragmentWrapper('TestOrderListing'), Object.values(testOrdersById));
  declarativeWriteRESTObjsToApollo('Run', Object.values(runsById));
  writeFragmentsToCache(
    getFragmentWrapper('StageEntrySummary'),
    Object.values(stageEntrySummariesGraphQLById)
  );

  return {
    searchId,
    status,
    ids,
    groupItemIds,
    itemResults,
    total,
    isExact,
    timedOut,
    correlationId,
    scoresById,
    groups,
    nextToken,
    attributes,
  };
}

export async function doGlobalSearch(
  searchParams: SearchParams,
  type: SearchType,
  searchSource?: string,
  requestConsistency?: boolean
): Promise<GlobalSearchResponse | AsyncSearchResponse> {
  const searchParamsWithDefault = populateSearchParamWithDefaultSort(searchParams, type);
  return await GlobalSearchDAO.doGlobalSearch(searchParamsWithDefault, searchSource, requestConsistency);
}
